import { Component, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { PlaceService } from '@app/modules/model/svcplace';
import { PrintDevice } from '@app/modules/model/printer';
import { PrintProduct } from '@app/modules/model/printproduct';

import { AppConstants } from '@app/app.constants';
import { languageService, languageSupport } from '@app/modules/common/language';
import { alertService } from '@app/modules/common/alert';
import { printService } from '@app/modules/print';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';
import { syncService } from '@app/modules/sync';

/************************************/
/* USER COMPONENT                   */
/************************************/

@Component({
    selector: 'ui-printer-devices',
    templateUrl: 'ui-printer.html',
    styleUrls: [
      'ui-printer.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiPrinterDevices extends languageSupport implements OnInit, OnChanges, OnDestroy { 
    @Input('service') _service: PlaceService = null;
    @Input('editdevice') _editdevice: any = null;

    /*****************************/
    /* COMPONENT DATA SHARING    */
    /*****************************/

    private get EditDevice(){
        return this._editdevice;
    }

    DiscardChanges(){
        this._RefreshDevices();
        this.LoadPrinterForm();
    }

    @Output('OnUpdate') _onupdate = new EventEmitter <any> ();
    private set EditDevice(value){
        this._editdevice = value;

        this._onupdate.next();
        this.change.markForCheck();
    }

    @Output('OnChange') _onchange = new EventEmitter <any> ();
    OnViewChanged(){
        this.change.markForCheck();
        this._onchange.emit();
    }

    get IsValid(){
        return true;
    }

    private _isdirty = false;
    get IsDirty(){
        if (!this.IsValid){
            return false;   // do not consider if its not valid
        }
        
        return this._isdirty;
    }

    set IsDirty(value){
        this._isdirty = value;
        if (value == false){
            this.PrinterForm.markAsPristine();
        }

        this.OnViewChanged();
    }

    private _printers: Array<PrintDevice> = [];
    get PlacePrinters(){
        return this._printers || [];
    }

    private _products: Array <PrintProduct> = null;
    get PrinterProducts(){
        return this._products;
    }

    set PrinterProducts(value){
        if (value != this._products){
            this._products = value;
        }

        this.OnViewChanged();
    }

    private _availableDevices: Array<string> = [];
    private _devicesLoaded = false;
    // Set once the first _LoadAvailableDevices call has had a chance to
    // decide the initial manual / list mode for this edit session. Further
    // refetches (e.g. the user reopening the dropdown) leave the flag
    // alone so they don't override a toggle the user already did by hand.
    private _manualDeviceInitialized = false;
    get AvailableDevices(){
        return this._availableDevices;
    }

    private _manualDevice = false;
    get IsManualDevice(){
        return this._manualDevice;
    }

    // Keep per-mode history so toggling between list/manual does not
    // erase a value the user (or the DB) already provided. Each mode
    // remembers the last value it held; on toggle we restore it.
    private _lastManualDevice: string = null;
    private _lastListDevice: string = null;

    OnToggleManualDevice(){
        let _current = this.PrinterForm.get('device').value || '';

        // save the current value under the mode we are leaving
        if (this._manualDevice){
            this._lastManualDevice = _current;
        }
        else {
            this._lastListDevice = _current;
        }

        this._manualDevice = !this._manualDevice;

        // restore the last value we had in the mode we are entering;
        // if there is no history yet, seed it with the current value
        // so toggling back and forth never loses what was there.
        let _restored: string;
        if (this._manualDevice){
            _restored = (this._lastManualDevice != null) ? this._lastManualDevice : _current;
        }
        else {
            _restored = (this._lastListDevice != null) ? this._lastListDevice : _current;
        }

        // in list mode, if the device list is loaded and the restored
        // value is not selectable, fall back to empty so the dropdown
        // does not show a stale selection. if the list is not loaded
        // yet we keep the value — _LoadAvailableDevices reconciles it.
        if (!this._manualDevice && this._devicesLoaded && _restored && !this._availableDevices.includes(_restored)){
            _restored = '';
        }

        this.PrinterForm.get('device').setValue(_restored);
        this.PrinterForm.get('device').markAsDirty();
        this.PrinterForm.markAsDirty();
        this.OnViewChanged();
    }

    OnDeviceSelectChange(event){
        let _value = event && event.target ? event.target.value : null;
        this.PrinterForm.get('device').setValue(_value || '');
        this.PrinterForm.get('device').markAsDirty();
        this.PrinterForm.markAsDirty();
        this.OnViewChanged();
    }

    // Guard against overlapping fetches. Lives inside _LoadAvailableDevices
    // so every entry point (OnDeviceSelectOpen, OnAddDevice, OnSetDevice)
    // shares the same protection — otherwise a user opening the dropdown
    // while the initial load is still in flight would kick off a second
    // concurrent FetchAvailablePrinters over the same WS.
    private _loadingDevices = false;
    OnDeviceSelectOpen(){
        this._LoadAvailableDevices();
    }

    private async _LoadAvailableDevices(){
        if (this._loadingDevices){
            return;
        }
        this._loadingDevices = true;

        try {
            let _service: any = this._service;
            let _place: any = this._service ? this._service.place : null;

            // If the edited rasppi has no WS, fall back to any service of the session
            // place that has WS configured. FetchAvailablePrinters also tries any
            // already-open local connection (tracked in deviceService).
            let _session = this.sync && this.sync.Session ? this.sync.Session : null;
            let _sessionPlace = _session ? _session.place : null;
            if ((!_service || !_service.wshost || !_service.wsport) && _sessionPlace){
                for (let _svc of (_sessionPlace.services || [])){
                    if (_svc.wshost && _svc.wsport){
                        _service = _svc;
                        _place = _sessionPlace;
                        break;
                    }
                }
            }

            try {
                this._availableDevices = await this.printer.FetchAvailablePrinters(_place, _service);
            }
            catch(error){
                console.error("[UI-PRINTER] Error fetching available printers", error);
                this._availableDevices = [];
            }
            this._devicesLoaded = true;

            // Only decide manual vs list mode on the FIRST successful load
            // of this edit session. Later refetches must not clobber a
            // toggle the user already made — otherwise flipping to list
            // mode and then triggering a refetch would bounce back to
            // manual when the saved device isn't in the list.
            //
            // Regla del modo inicial: si la impresora ya tiene nombre de
            // dispositivo guardado, empezamos en MODO MANUAL aunque el
            // nombre aparezca en la lista del CommandViewer. El usuario
            // tecleo ese nombre — preservamos su experiencia previa al
            // feature del dropdown y evitamos el "salto" sorpresa a list
            // mode al abrir la config. Solo las impresoras NUEVAS (sin
            // nombre todavia) arrancan en list mode para que el usuario
            // descubra el dropdown como entry point natural.
            if (!this._manualDeviceInitialized){
                let _device = this.EditDevice ? this.EditDevice.device : null;
                this._manualDevice = !!_device;
                this._manualDeviceInitialized = true;
            }

            this.OnViewChanged();
        }
        finally {
            this._loadingDevices = false;
        }
    }

    /*****************************/
    /* COMPONENT DATA ACCESS     */
    /*****************************/

    private _printerForm = null;
    get PrinterForm(){
        if (this._printerForm == null){
            this.LoadPrinterForm();
        }
        return this._printerForm;
    }

    private get _PrinterProducts(){
        let _products = [];
        for(let _product of this.EditDevice.products){
            if (_product.IsValid){
                _products.push(_product);
            }
        }
        return _products;
    }

    private LoadPrinterForm(){
        let _type = null;
        let _products = null;
        let _name = null;
        let _device = null;
        let _width = null;
        let _font = 12;
        let _top = 0;
        let _right = 0;
        let _bottom = 0;
        let _left = 0;
        let _updates = false;
        let _posudts = false;
        let _oncash = false;
        let _oncard = false;
        let _copies = null;
        let _bold = false;
    
        if (this.EditDevice){
            _type = this.EditDevice.type;
            _name = this.EditDevice.name;
            _products = this._PrinterProducts;
            _device = this.EditDevice.device;
            _width = this.EditDevice.width;
            _font = this.EditDevice.font;
            _top = this.EditDevice.top;
            _right = this.EditDevice.right;
            _bottom = this.EditDevice.bottom;
            _left = this.EditDevice.left;
            _updates = this.EditDevice.updates;
            _posudts = this.EditDevice.posudts;
            _oncash = this.EditDevice.oncash;
            _oncard = this.EditDevice.oncard;
            _copies = this.EditDevice.copies;
            _bold =  this.EditDevice.bold;
        }

        if (this._printerForm == null){
            this._printerForm = new FormGroup({
                type: new FormControl(_type, [
                    Validators.required
                ]),
                products: new FormControl(_products, [
                    // no validators required
                ]),
                name: new FormControl(_name, [
                    // no validators required
                ]),
                device: new FormControl(_device, [
                    Validators.required
                ]),
                width: new FormControl(_width, [
                    Validators.required
                ]),
                font: new FormControl(_font, [
                    Validators.required
                ]),
                top: new FormControl(_top, [
                    // no validators required
                ]),
                right: new FormControl(_right, [
                    // no validators required
                ]),
                bottom: new FormControl(_bottom, [
                    // no validators required
                ]),
                left: new FormControl(_left, [
                    // no validators required
                ]),
                updates: new FormControl(_updates, [
                    // no validators required
                ]),
                posudts: new FormControl(_posudts, [
                    // no validators required
                ]),
                oncash: new FormControl(_oncash, [
                    // no validators required
                ]),                
                oncard: new FormControl(_oncard, [
                    // no validators required
                ]),                
                copies: new FormControl(_copies, [
                    // no validators required
                ]),
                bold: new FormControl(_bold, [
                    // no validators required
                ])
            })
        }
        else {
            this._printerForm.patchValue({
                type: _type,
                products: _products,
                name: _name, 
                device: _device,
                width: _width,
                font: _font,
                top: _top,
                right: _right,
                bottom: _bottom,
                left: _left,
                updates: _updates,
                posudts: _posudts,
                oncash: _oncash,
                oncard: _oncard,
                copies: _copies,
                bold: _bold
            });
        }

        this._printerForm.markAsPristine();
        setTimeout(() => {
            this._printerForm.markAsPristine();
        }, 0)

        this.change.markForCheck();
    }

    /****************************/
    /* STARTS HERE              */
    /****************************/

    private _service_subscription = null;

    private _RefreshDevices(){
        this._printers = this._PlacePrinters;   
        this.IsDirty = false;  

        this.OnViewChanged();       // update devices view on parent
    }

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, private printer: printService, private data: dataService, public view: viewService, private sync: syncService) {
        super(lang, change);
    }

    private get _PlacePrinters(){
        let _printers = [];
        if (this._service){
            for(let _printer of this._service.printers)
            if (_printer.IsValid){
                _printers.push(_printer);
            }
        }

        return _printers;
    }

    ngOnInit(){
        this._RefreshDevices();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('_service' in changes){     // only refresh on service change
            this._RefreshDevices();    
            
            if (this._service_subscription){
                this._service_subscription.unsubscribe();
                this._service_subscription = null;
            }
    
            this._service_subscription = this._service.OnRefresh.subscribe(
            data => {
                if (!this.IsDirty){
                    this._RefreshDevices();
                }
                this.change.markForCheck();
            });    
        }
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._service_subscription){
            this._service_subscription.unsubscribe();
            this._service_subscription = null;
        }        
    }

    /****************************/
    /* EVENT HANDLERS           */
    /****************************/

    private _expand = false;
    get ExpandPrinters(){
        return this._expand;
    }

    @ViewChild('devices', { static: false }) _devices: ElementRef;
    OnExpandPrinters(){
        this._expand = !this._expand;

        if (this._devices){
            let _devices = this._devices.nativeElement;
            if (this._expand){
                _devices.style.height = _devices.scrollHeight + 'px'; 
                setTimeout(() => {
                    _devices.style.height = 'auto';
                }, 200);                
            }
            else {
                _devices.style.height = _devices.scrollHeight + 'px';
                _devices.offsetHeight;                
                _devices.style.height = '0px';     
            }    
        }

        this.change.markForCheck();
    }

    OnShowProducts(){
        this._ApplyPrinterForm();
        this.PrinterProducts = this.PrinterForm.get('products').value || [];

        this.OnViewChanged();
    }

    OnAddProducts(products){
        this.OnHideProducts();
        this.PrinterForm.get('products').setValue(products);

        this.PrinterForm.markAsDirty();
        this.change.markForCheck();
    }

    OnHideProducts(){
        this.PrinterProducts = null;
        this.change.markForCheck();
    }

    private _ResetDeviceToggleState(){
        this._devicesLoaded = false;
        this._manualDeviceInitialized = false;
        this._availableDevices = [];
        this._lastManualDevice = null;
        this._lastListDevice = null;
    }

    OnAddDevice(){
        this._isdirty = true;

        let _device = new PrintDevice(null, this.data);
        if (_device){
            _device.rasppi = this._service;
            _device.type = 'BAR';
            _device.updates = false;
            _device.posudts = false;
            _device.oncash = false;
            _device.oncard = false;
            _device.width = AppConstants.defaultPrintWidth;
            _device.font = AppConstants.defaultPrintFont;
            _device.copies = 1;
            _device.bold = false;
            _device.status = 'AC';
        }

        this.EditDevice = _device;
        this.LoadPrinterForm();
        this.EditDevice['operation'] = 'add';

        let _idx = this._printers.indexOf(_device);
        if (_idx == -1){
            this._printers.unshift(this.EditDevice);
        }

        this._expand = true;
        this.change.markForCheck();

        this._ResetDeviceToggleState();
        this._LoadAvailableDevices();
    }

    OnSetDevice(device){
        this.EditDevice = device;
        this.LoadPrinterForm();
        this.EditDevice['operation'] = 'set';

        this.change.markForCheck();

        this._ResetDeviceToggleState();
        this._LoadAvailableDevices();
    }

    private _PrintersUpdated(){
        let _placeprinters = this.PlacePrinters;
        if (_placeprinters.length == this._printers.length){
            return _placeprinters.every(_printer => {
                return this._printers.includes(_printer);
            })
        }
        return false;
    }

    private _DelDevice(_device){
        _device.status = 'DE';

        let _idx = this._printers.indexOf(_device);
        if (_idx != -1){
            this._printers.splice(_idx, 1);
        }

        this.EditDevice = null;
        this._isdirty = this._PrintersUpdated();
        this.change.markForCheck();
    }

    async OnDelDevice(device){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_printer_delete_confirm'),
            message: this.lang.tr('@place_printer_delete_message', [ device.name || this.lang.tr('@place_device_printer_noname') ]),
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then(async (detail:any) => {
            if (detail.data == true){
                this._DelDevice(device);
            }
        });

        await alert.present();
    }

    OnUpdatesChange(){
        let _control = this.PrinterForm.get('posudts');
        if (this.PrinterForm.get('updates').value){
            _control.enable();
        }
        else {
            _control.value = false;
            _control.disable();
        }
    }

    /****************************/
    /* APPLY CHANGES            */
    /****************************/

    private _ApplyPrinterForm(){
        this.EditDevice.name = this.PrinterForm.get('name').value;
        this.EditDevice.device = this.PrinterForm.get('device').value;
        this.EditDevice.type = this.PrinterForm.get('type').value;
        this.EditDevice.updates = this.PrinterForm.get('updates').value;
        this.EditDevice.posudts = this.PrinterForm.get('posudts').value;
        this.EditDevice.oncash = this.PrinterForm.get('oncash').value;
        this.EditDevice.oncard = this.PrinterForm.get('oncard').value;
        this.EditDevice.width = this.PrinterForm.get('width').value;
        this.EditDevice.font = this.PrinterForm.get('font').value;
        this.EditDevice.top = this.PrinterForm.get('top').value;
        this.EditDevice.right = this.PrinterForm.get('right').value;
        this.EditDevice.bottom = this.PrinterForm.get('bottom').value;
        this.EditDevice.left = this.PrinterForm.get('left').value;
        this.EditDevice.copies = this.PrinterForm.get('copies').value || 1;
        this.EditDevice.bold = this.PrinterForm.get('bold').value;

        if (this.EditDevice.name){
            this.EditDevice.name = this.EditDevice.name.trim();
        }

        if (this.EditDevice.device){
            this.EditDevice.device = this.EditDevice.device.trim();
        }
    }

    private _ApplyPrinterProducts(products){
        this._ApplyPrinterForm();

        let _printproducts: Array <PrintProduct> = [];

        // update the printproduct information (printer)
        for(let _printproduct of products){
            _printproduct.printer = this.EditDevice;
            _printproducts.push(_printproduct);
        }

        // include all products in the final list
        for(let _printproduct of _printproducts){
            this.EditDevice.AddProduct(_printproduct);
        }

        // remove products not in the final list
        for(let _printproduct of this.EditDevice.products){
            if (!_printproducts.includes(_printproduct)){
                _printproduct.status = 'DE';
            }
        }

        // refresh the products view (item = atajo/select o producto; con atajo
        // product es null, así que refrescamos a través de item).
        for(let _printerproduct of this.EditDevice.products){
            let _ref = _printerproduct.item;
            if (_ref){
                _ref.DoRefresh('PRINTERPRODUCT');
            }
        }
    }

    /****************************/
    /* COMMIT ACTIONS           */
    /****************************/

    OnAcceptDevice(){
        this._ApplyPrinterProducts(this.PrinterForm.get('products').value)
        this.PrinterForm.markAsPristine();

        this.EditDevice = null;
        this.IsDirty = true;
    }

    OnCancelDevice(){
        this.PrinterForm.markAsPristine();

        if (this.EditDevice['operation'] == 'add'){
            this._DelDevice(this.EditDevice);
        }

        this.EditDevice = null;
    }
}
